import React, { useEffect } from "react";
import { Field } from "formik";
import { Checkbox } from "components/ui";
import { SignUpPolicyLabel } from "components/shared";
import CoreContractPolicyLabel from "./CoreContractPolicyLabel";
import { CONTRACT_LINK_CLASS_NAME } from "constants/moduleConstants/contract.constant";
import { useTranslation } from "react-i18next";

const ContractPolicyCheckboxItem = ({ invalid, errorMessage, children }) => (
  <div className="text-left">
    {children}
    {invalid && errorMessage ? (
      <p className="mt-1.5 text-sm leading-snug text-red-500">{errorMessage}</p>
    ) : null}
  </div>
);

const ContractPolicyCheckboxes = ({
  coreContracts = [],
  supplementalContracts = [],
  loading = false,
  useLegacyPolicy = true,
  values,
  errors,
  touched,
  setFieldValue,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (loading || useLegacyPolicy) return;

    supplementalContracts.forEach((contract) => {
      const fieldName = `contracts.${contract.contractversionid}`;
      if (values?.contracts?.[contract.contractversionid] === undefined) {
        setFieldValue(fieldName, false, false);
      }
    });
  }, [
    supplementalContracts,
    loading,
    useLegacyPolicy,
    setFieldValue,
    values?.contracts,
  ]);

  if (loading) {
    return null;
  }

  if (useLegacyPolicy) {
    return (
      <div className="mb-8">
        <ContractPolicyCheckboxItem
          invalid={errors.policy && touched.policy}
          errorMessage={errors.policy}
        >
          <Field
            name="policy"
            component={Checkbox}
            children={<SignUpPolicyLabel />}
          />
        </ContractPolicyCheckboxItem>
      </div>
    );
  }

  const contractsError =
    typeof errors.contracts === "string" ? errors.contracts : null;

  return (
    <div className="mb-8 flex flex-col gap-5">
      {coreContracts.length ? (
        <ContractPolicyCheckboxItem
          invalid={errors.corePolicy && touched.corePolicy}
          errorMessage={errors.corePolicy}
        >
          <Field name="corePolicy" component={Checkbox}>
            <CoreContractPolicyLabel coreContracts={coreContracts} />
          </Field>
        </ContractPolicyCheckboxItem>
      ) : null}

      {supplementalContracts.map((contract) => {
        const fieldName = `contracts.${contract.contractversionid}`;
        const displayName =
          contract.description ||
          t(`contracts.whitelabelTypes.${contract.type}.label`, {
            defaultValue: String(contract.type),
          });
        const touchedField =
          touched?.contracts?.[contract.contractversionid];
        const fieldError = errors?.contracts?.[contract.contractversionid];

        return (
          <ContractPolicyCheckboxItem
            key={contract.contractversionid}
            invalid={Boolean(fieldError && touchedField)}
            errorMessage={fieldError}
          >
            <Field name={fieldName} component={Checkbox}>
              <span>
                {t("auth.signUp.form.contracts.approvePrefix")}{" "}
                <a
                  href={contract.document}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={CONTRACT_LINK_CLASS_NAME}
                  onClick={(event) => event.stopPropagation()}
                >
                  {displayName}
                </a>{" "}
                {t("auth.signUp.form.contracts.approveSuffix")}
              </span>
            </Field>
          </ContractPolicyCheckboxItem>
        );
      })}

      {contractsError ? (
        <p className="text-sm text-red-500">{contractsError}</p>
      ) : null}
    </div>
  );
};

export default ContractPolicyCheckboxes;
